/**
 * Crop/Edit Toggle Button
 * 
 * Toggle above timeline to enable/disable crop and scale editing
 * When active: user can drag and scale media items
 * When inactive: normal view mode
 */

import React from 'react';
import { Button, Group, Text, Tooltip } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrop, faArrowsAlt } from '@fortawesome/free-solid-svg-icons';

interface CropEditToggleProps {
  isActive: boolean;
  onToggle: (active: boolean) => void;
  disabled?: boolean;
  hasMediaItems?: boolean;
  hasSelectedMedia?: boolean; // NEW: whether a media item is selected
}

const CropEditToggle: React.FC<CropEditToggleProps> = ({
  isActive,
  onToggle,
  disabled = false,
  hasMediaItems = true,
  hasSelectedMedia = false,
}) => {
  const isDisabled = disabled || !hasMediaItems || (!isActive && !hasSelectedMedia);

  return (
    <Tooltip 
      label={
        !hasMediaItems
          ? 'Add media items to enable crop mode'
          : !hasSelectedMedia && !isActive
          ? 'Select a media item from the timeline first'
          : isActive
          ? 'Crop/Edit mode active - Click a media item to adjust it'
          : 'Enable crop/edit mode - Then click media to adjust position and scale'
      }
    >
      <Button
        onClick={() => onToggle(!isActive)}
        disabled={isDisabled}
        size="lg"
        variant={isActive ? 'filled' : 'subtle'}
        color={isActive ? 'green' : 'gray'}
        title={
          !hasMediaItems
            ? 'Add media items to enable crop mode'
            : !hasSelectedMedia && !isActive
            ? 'Select a media item from the timeline first'
            : isActive
            ? 'Crop/Edit mode active - Click a media item to adjust it'
            : 'Enable crop/edit mode - Then click media to adjust position and scale'
        }
        style={{
          width: '42px',
          height: '42px',
          padding: 0,
          zIndex: 10,
          borderRadius: '6px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          backgroundColor: isActive ? '#22c55e' : undefined,
        }}
      >
        <FontAwesomeIcon 
          icon={faCrop} 
          style={{ 
            fontSize: '20px',
            color: isActive ? 'white' : undefined,
          }} 
        />
      </Button>
    </Tooltip>
  );
};

export default CropEditToggle;
